
// This file is automatically generated. Do not edit it directly.
import { createClient } from '@supabase/supabase-js';
import type { Database } from './types';

// Use the same hard-coded values for development, preview and production
const SUPABASE_URL = "https://godsqyliromtwollxpbr.supabase.co";
const SUPABASE_PUBLISHABLE_KEY = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImdvZHNxeWxpcm9tdHdvbGx4cGJyIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NDM1ODE4NDMsImV4cCI6MjA1OTE1Nzg0M30.zMDxI67t7nlKWqNoGV_O-2H8LjK-jX6jUrpTgk6HlP4";

// Import the supabase client like this:
// import { supabase } from "@/integrations/supabase/client";

export const supabase = createClient<Database>(SUPABASE_URL, SUPABASE_PUBLISHABLE_KEY, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true
  }
});

// Enhanced connection check with more detailed logging
const checkConnection = async () => {
  // Determine environment for logging purposes
  const hostname = typeof window !== 'undefined' ? window.location.hostname : 'server';
  const isProd = hostname !== 'localhost' && 
                !hostname.includes('lovableproject.com');
  
  console.log(`🔌 Supabase connection check [ENV: ${isProd ? 'PRODUCTION' : 'DEVELOPMENT/PREVIEW'}]`);
  console.log(`🔗 Supabase URL: ${SUPABASE_URL}`);
  console.log(`🔑 Supabase key (last 10 chars): ...${SUPABASE_PUBLISHABLE_KEY.slice(-10)}`);
  
  try {
    // First check authentication status
    const { data: { user } } = await supabase.auth.getUser();
    console.log(`👤 Auth status: ${user ? `Authenticated as ${user.email}` : 'Not authenticated (anonymous)'}`);
    
    // Check connection with languages table first (which should be accessible to anonymous users)
    console.log(`🔍 Testing anonymous access to 'languages' table...`);
    const { data: languageData, error: languageError } = await supabase
      .from('languages')
      .select('code')
      .limit(3);
    
    if (languageError) {
      console.error('❌ CRITICAL ERROR: Cannot access languages table:', languageError);
      console.error('⚠️ This indicates a severe configuration problem with RLS or database access!');
      return;
    }
    
    console.log(`✅ Languages table access successful! Found ${languageData?.length || 0} languages`);
    
    // Now check critical tables for our application
    const tables = ['tours', 'exhibits', 'tour_translations', 'exhibit_translations'] as const;
    console.log(`🔍 Testing access to critical tables as ${user ? 'authenticated user' : 'anonymous user'}...`);
    
    for (const table of tables) {
      const { data: tableData, error: tableError } = await supabase
        .from(table)
        .select('id')
        .limit(1);
      
      if (tableError) {
        console.error(`❌ ERROR accessing ${table}:`, tableError);
        console.error(`⚠️ This might indicate missing RLS policies for ${user ? 'authenticated' : 'anonymous'} access!`);
      } else {
        console.log(`✅ ${table} access: SUCCESS (found ${tableData?.length || 0} records)`);
      }
    }
    
    // Raw query to test and log complete response
    console.log('🔍 Running raw query to test complete data return...');
    const { data: rawToursData, error: rawToursError } = await supabase
      .from('tours')
      .select(`
        id,
        name,
        status,
        cover_image,
        tour_translations (language_code, title)
      `)
      .limit(3);
    
    if (rawToursError) {
      console.error('❌ Raw tours query failed:', rawToursError);
    } else {
      console.log(`✅ Raw tours query succeeded! Data:`, rawToursData);
    }
    
  } catch (err) {
    console.error('❌ Supabase comprehensive check error:', err);
  }
};

// Run the enhanced connection check when the client is imported
// This will help identify if the connection is working in both preview and production
// Using setTimeout to ensure this runs after the main JS execution
setTimeout(() => {
  try {
    console.log('🚀 Running Supabase connection diagnostics...');
    checkConnection();
  } catch (e) {
    console.error('❌ Failed to run diagnostics:', e);
  }
}, 1000);
